import React, { FC, useEffect, useState } from "react";
import { Button, Card, Container } from "react-bootstrap";
import { useLazyQuery, useMutation, useSubscription } from "@apollo/client";
import {
    Project as ProjectType,
    Revision,
    RevisionsData,
    RevisionsVariables,
    RevisionVariables,
    SortingMode
} from "../../models/types";
import {
    GET_PRODUCT_META_DATA,
    GET_PROJECT_META_DATA,
    GET_REVISIONS
} from "../../graphql/queries";
import { REMOVE_REVISION } from "../../graphql/mutations";
import { useLocalState, useSortingState } from "../../graphql/hooks";
import { ReleaseNotesModal } from "../modals/ReleaseNotesModal";
import { DevelopmentRevisionItem } from "../simple/DevelopmentRevisionItem";
import { AlertModal } from "../modals/AlertModal";
import { sortItems } from "../../common/Helpers";
import { REVISION_CHANGED_SUBSCRIPTION } from "../../graphql/subscriptions";
import "./views.scss";

interface Props {
    project: ProjectType;
    showChildren: boolean;
    updateLoaded: (loaded: boolean) => void;
}

const ProjectImplementation: FC<Props> = ({
    project,
    showChildren,
    updateLoaded
}) => {
    const state = useLocalState();
    const [revisionsLoaded, updateRevisionsLoaded] = useState(false);
    const [sortedRevisions, updateSortedRevisions] = useState<Revision[]>([]);
    const [showRemoveAlert, updateShowRemoveAlert] = useState(false);
    const [toBeRemoved, updateToBeRemoved] = useState<number | undefined>(
        undefined
    );
    const [releaseNotesRevisionId, updateReleaseNotesRevisionId] = useState(-1);
    const [showReleaseNotes, updateShowReleaseNotes] = useState(false);
    const sortingState = useSortingState();
    const [
        fetchRevisionData,
        // @ts-ignore
        { loading, data: { revisions } = {} }
    ] = useLazyQuery<RevisionsData, RevisionsVariables>(GET_REVISIONS, {
        variables: { projectId: project.id, searchTerm: state.searchTerm },
        fetchPolicy: "network-only"
    });

    const [removeRevision] = useMutation<RevisionVariables>(REMOVE_REVISION);

    useEffect(() => {
        if (showChildren && !revisions) {
            fetchRevisionData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showChildren, revisions]);

    useEffect(() => {
        if (!loading && revisions) {
            updateRevisionsLoaded(true);
            updateLoaded(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, revisions]);

    useEffect(() => {
        if (revisions) {
            const sortingMode =
                sortingState.sortingPriorities[2] || SortingMode.None;
            if (sortingMode === SortingMode.None) {
                updateSortedRevisions(revisions);
                return;
            }
            const sorted = [...revisions];
            sorted.sort((a, b) => sortItems(a, b, sortingMode));
            updateSortedRevisions(sorted);
        }
    }, [sortingState.sortingPriorities, revisions, project]);

    useSubscription(REVISION_CHANGED_SUBSCRIPTION, {
        variables: { projectId: project.id },
        onData: ({
            data: { data: { revisionChangedNotification } = {} },
            client
        }) => {
            console.log(
                "[DEBUG] revisionChangedNotification: ",
                revisionChangedNotification
            );
            const currentRevisionData = client.cache.readQuery<
                RevisionsData,
                RevisionsVariables
            >({ variables: { projectId: project.id }, query: GET_REVISIONS });

            const currentRevisions = currentRevisionData?.revisions;

            if (currentRevisions && revisionChangedNotification) {
                if (revisionChangedNotification.action === 0) {
                    client.cache.writeQuery<RevisionsData, RevisionsVariables>({
                        query: GET_REVISIONS,
                        data: {
                            revisions: [
                                ...currentRevisions,
                                revisionChangedNotification.revision
                            ]
                        },
                        variables: { projectId: project.id }
                    });
                } else {
                    if (revisionChangedNotification.action === 1) {
                        const currentRevision = sortedRevisions?.find(
                            revision =>
                                revision.id ===
                                revisionChangedNotification.rId.toString()
                        );
                        console.log(
                            "[DEBUG] currentRevision ",
                            currentRevision
                        );
                        const newRevision = {
                            ...revisionChangedNotification.revision,
                            ...{
                                impressions: currentRevision?.impressions,
                                gameplay: currentRevision?.gameplay,
                                endScreen: currentRevision?.endScreen,
                                userImpressions:
                                    currentRevision?.userImpressions,
                                userGameplay: currentRevision?.userGameplay,
                                userEndScreen: currentRevision?.userEndScreen
                            }
                        };
                        console.log("[DEBUG] newRevision ", newRevision);
                        const newRevisions = currentRevisions.filter(
                            revision =>
                                revision.id !==
                                revisionChangedNotification.rId.toString()
                        );
                        client.cache.writeQuery<
                            RevisionsData,
                            RevisionsVariables
                        >({
                            query: GET_REVISIONS,
                            data: {
                                revisions: [...newRevisions, newRevision]
                            },
                            variables: { projectId: project.id }
                        });
                    } else if (revisionChangedNotification.action === 2) {
                        const newRevisions = currentRevisions.filter(
                            revision =>
                                revision.id !==
                                revisionChangedNotification.rId.toString()
                        );
                        client.cache.writeQuery<
                            RevisionsData,
                            RevisionsVariables
                        >({
                            query: GET_REVISIONS,
                            data: {
                                revisions: newRevisions
                            },
                            variables: { projectId: project.id }
                        });
                    }
                }
            }
        }
    });

    const removeRevisionById = async (id: number) => {
        try {
            await removeRevision({
                variables: { revisionId: id },
                refetchQueries: [
                    {
                        query: GET_PRODUCT_META_DATA,
                        variables: { productId: project.product?.id }
                    },
                    {
                        query: GET_PROJECT_META_DATA,
                        variables: { projectId: project.id }
                    },
                    {
                        query: GET_REVISIONS,
                        variables: { projectId: project.id }
                    }
                ]
            });
            if (toBeRemoved) {
                updateToBeRemoved(undefined);
            }
        } catch (error) {
            console.log("[DEBUG] removeRevisionById error ", error);
        }
    };

    const developmentRevision: Revision | undefined =
        sortedRevisions[0] || undefined;

    if (!developmentRevision || !revisions || !revisionsLoaded) {
        return null;
    }

    return (
        <Container className="fullSizeChild m-0 p-0">
            <Card className="text-inverse no-border medium-font">
                <DevelopmentRevisionItem
                    revision={developmentRevision}
                    projectId={project.id}
                    showReleaseNotes={(id: number) => {
                        updateReleaseNotesRevisionId(id);
                        updateShowReleaseNotes(true);
                    }}
                    onRemove={
                        developmentRevision
                            ? () => {
                                  updateToBeRemoved(developmentRevision.id);
                                  updateShowRemoveAlert(true);
                              }
                            : undefined
                    }
                />
            </Card>
            {showReleaseNotes ? (
                <ReleaseNotesModal
                    onClose={() => updateShowReleaseNotes(false)}
                    show={showReleaseNotes}
                    revisionId={releaseNotesRevisionId}
                />
            ) : null}
            {showRemoveAlert ? (
                <AlertModal
                    header={<h5>Remove Revision</h5>}
                    component="Removing the revision also removes its builds!"
                    visible={() => updateShowRemoveAlert(false)}
                    footer={
                        <Button
                            variant="danger"
                            className="mt-2"
                            onClick={() => {
                                if (toBeRemoved) {
                                    removeRevisionById(toBeRemoved);
                                    updateShowRemoveAlert(false);
                                }
                            }}
                        >
                            Continue to Remove
                        </Button>
                    }
                />
            ) : null}
        </Container>
    );
};

export const Project = React.memo(ProjectImplementation);
